import { salesBomPartsMatching } from '@constants/URLs';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDownloadFile } from '@hooks/useDownloadFile';
import { Typography, useTheme } from '@mui/material';
import { useManageProductPageAnalytics } from '@services/useManageProductPageAnalytics';
import { useTranslation } from 'react-i18next';
import { HeaderText } from '../../pages/CreateNewProductPage/styles';
import { GetConfigurationMatchReportResponse } from '../../services/productInventory';
import { StyledMUISecondaryButton as Button } from '../Button';
import { ExternalLink } from '../Button/ExternalLink';
import { Column, Item, Result } from './StyledComponents';

interface MatchReportTableProps {
  productName: string;
  data: GetConfigurationMatchReportResponse;
}

export function MatchReportTable({ productName, data }: Readonly<MatchReportTableProps>) {
  const theme = useTheme();
  const { t } = useTranslation(['createProduct']);
  const { sendMatchReportDownloadEvent, sendLearnMoreSalesBOMEvent } = useManageProductPageAnalytics();

  const { mutate: download, isLoading } = useDownloadFile({
    path: data.matchReportPresignedURL,
  });

  const { unsuccessfullyMatched, successfullyMatched, totalPartsUploaded } = data;
  const percentage = (small: number, total: number) => ((small / total) * 100).toFixed(2);
  const shouldAlert = unsuccessfullyMatched !== 0;

  return (
    <Column>
      <HeaderText data-testid="left-header" variant="body1">
        {t('createProduct:matchReport.title', { productName })}
      </HeaderText>
      <hr />
      <Item>
        <Typography>{t('createProduct:matchReport.salesBomPartsMatchedToCadParts')}:</Typography>
        <Result data-testid="percentage-matched-text">{percentage(successfullyMatched, totalPartsUploaded)}%</Result>
      </Item>
      <Item sx={{ width: '100%' }}>
        <Typography>{t('createProduct:matchReport.matchedParts')}:</Typography>
        <Result data-testid="successfully-matched-text">{successfullyMatched}</Result>
      </Item>
      <Item alert={shouldAlert}>
        <Typography>{t('createProduct:matchReport.unmatchedParts')}:</Typography>
        <Result data-testid="unsuccessfully-matched-text">
          {unsuccessfullyMatched}{' '}
          {shouldAlert && <FontAwesomeIcon icon={faTriangleExclamation} color={theme.palette.warning.main} />}
        </Result>
      </Item>
      <Item>
        <Typography>{t('createProduct:matchReport.totalPartsInSalesBom')}:</Typography>
        <Result data-testid="total-parts-uploaded-text">{totalPartsUploaded}</Result>
      </Item>

      <Item alignItems="start" direction="column">
        <Button
          color="primary"
          disabled={isLoading}
          onClick={() => {
            download();
            sendMatchReportDownloadEvent();
          }}
        >
          {t('createProduct:matchReport.downloadMatchReport')}
        </Button>
        <ExternalLink
          data-testid="learn-more-matching"
          href={salesBomPartsMatching}
          onClick={
            /* istanbul ignore next */ () => {
              sendLearnMoreSalesBOMEvent();
            }
          }
        >
          {t('createProduct:sbom.learnMoreSBOM')}
        </ExternalLink>
        <hr />
        <Typography>{t('createProduct:matchReport.matchReportDescription')}</Typography>
        <Typography>{t('createProduct:matchReport.matchReportDescription2')}</Typography>
      </Item>
    </Column>
  );
}
